import React, { useState } from 'react';
import { signIn, completeNewPassword, forgotPassword, confirmForgotPassword } from '../cognito-auth';
import { handleLoginButton as azureLogin } from '../auth';
import designerLogo from './assets/Designer_logo.png';

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    fontFamily: 'Arial, sans-serif',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '32px',
  },
  logoImg: { height: '40px' },
  logoText: { fontSize: '22px', fontWeight: 'bold', color: '#333', margin: 0 },
  card: {
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
    display: 'flex',
    overflow: 'hidden',
    width: '700px',
    maxWidth: '95vw',
  },
  panel: {
    flex: 1,
    padding: '40px 32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  leftPanel: { borderRight: '1px solid #e0e0e0', backgroundColor: '#fafafa' },
  panelTitle: { fontSize: '14px', color: '#555', marginBottom: '20px', textAlign: 'center' },
  formTitle: { fontSize: '16px', fontWeight: 'bold', color: '#333', marginBottom: '6px', textAlign: 'center' },
  formSubtitle: { fontSize: '13px', color: '#666', marginBottom: '20px', textAlign: 'center' },
  azureBtn: {
    width: '100%', padding: '10px 0', backgroundColor: '#0078d4', color: '#fff',
    border: 'none', borderRadius: '4px', fontSize: '14px', cursor: 'pointer',
  },
  azureBtnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  label: { width: '100%', fontSize: '13px', color: '#444', marginBottom: '4px', marginTop: '12px' },
  input: {
    width: '100%', padding: '9px 10px', border: '1px solid #ccc',
    borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box', outline: 'none',
  },
  primaryBtn: {
    width: '100%', padding: '10px 0', backgroundColor: '#1a3a5c', color: '#fff',
    border: 'none', borderRadius: '4px', fontSize: '14px', cursor: 'pointer', marginTop: '20px',
  },
  primaryBtnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  linkBtn: {
    alignSelf: 'flex-end', fontSize: '12px', color: '#0078d4', cursor: 'pointer',
    marginTop: '4px', background: 'none', border: 'none', padding: 0,
  },
  backBtn: {
    alignSelf: 'flex-start', fontSize: '12px', color: '#0078d4', cursor: 'pointer',
    marginBottom: '12px', background: 'none', border: 'none', padding: 0,
  },
  errorMsg: {
    width: '100%', marginTop: '12px', padding: '8px 10px',
    backgroundColor: '#fdecea', border: '1px solid #f5c6cb',
    borderRadius: '4px', color: '#c0392b', fontSize: '13px',
  },
  successMsg: {
    width: '100%', marginTop: '12px', padding: '8px 10px',
    backgroundColor: '#e8f5e9', border: '1px solid #a5d6a7',
    borderRadius: '4px', color: '#2e7d32', fontSize: '13px',
  },
  hint: { width: '100%', marginTop: '8px', fontSize: '11px', color: '#888' },
};

// ─── Normal login form ────────────────────────────────────────────────────────
function LoginForm({ onLoginSuccess, onNewPasswordRequired, onForgotPassword }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setLoading(true);
    setError('');
    try {
      const result = await signIn(username.trim(), password);
      if (result && result.newPasswordRequired) {
        onNewPasswordRequired(result.cognitoUser, result.userAttributes);
      } else {
        onLoginSuccess(result);
      }
    } catch (err) {
      if (err.code === 'NotAuthorizedException') {
        setError('Incorrect username or password.');
      } else if (err.code === 'UserNotFoundException') {
        setError('User not found. Please contact your administrator.');
      } else if (err.code === 'UserNotConfirmedException') {
        setError('Account not confirmed. Please contact your administrator.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <label style={styles.label}>Username</label>
      <input style={styles.input} type="text" placeholder="Username" value={username}
        onChange={(e) => setUsername(e.target.value)} autoComplete="username" required />

      <label style={styles.label}>Password</label>
      <input style={styles.input} type="password" placeholder="Password" value={password}
        onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />

      <button type="button" style={styles.linkBtn} onClick={() => onForgotPassword(username.trim())}>
        Forgot your password?
      </button>

      <button type="submit" style={{ ...styles.primaryBtn, ...(loading ? styles.primaryBtnDisabled : {}) }} disabled={loading}>
        {loading ? 'Signing in…' : 'Sign In'}
      </button>
      {error && <div style={styles.errorMsg}>{error}</div>}
    </form>
  );
}

// ─── Set new password (first login) ──────────────────────────────────────────
function NewPasswordForm({ cognitoUser, userAttributes, onLoginSuccess }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    setError('');
    try {
      const userInfo = await completeNewPassword(cognitoUser, newPassword, userAttributes);
      onLoginSuccess(userInfo);
    } catch (err) {
      setError(err.message || 'Failed to set new password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <p style={styles.formTitle}>Set Your Password</p>
      <p style={styles.formSubtitle}>This is your first login. Please set a new password to continue.</p>

      <label style={styles.label}>New Password</label>
      <input style={styles.input} type="password" placeholder="New password" value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" required />

      <label style={styles.label}>Confirm Password</label>
      <input style={styles.input} type="password" placeholder="Confirm new password" value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" required />

      <p style={styles.hint}>Minimum 8 characters. Include uppercase, lowercase, numbers and symbols.</p>

      <button type="submit" style={{ ...styles.primaryBtn, ...(loading ? styles.primaryBtnDisabled : {}) }} disabled={loading}>
        {loading ? 'Setting password…' : 'Set Password & Sign In'}
      </button>
      {error && <div style={styles.errorMsg}>{error}</div>}
    </form>
  );
}

// ─── Forgot password — step 1: enter username ─────────────────────────────────
function ForgotPasswordForm({ initialUsername, onCodeSent, onBack }) {
  const [username, setUsername] = useState(initialUsername || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    setLoading(true);
    setError('');
    try {
      await forgotPassword(username.trim());
      onCodeSent(username.trim());
    } catch (err) {
      if (err.code === 'UserNotFoundException') {
        setError('User not found.');
      } else if (err.code === 'InvalidParameterException') {
        setError('Cannot reset password — email address is not verified. Please contact your administrator.');
      } else {
        setError(err.message || 'Failed to send reset code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <button type="button" style={styles.backBtn} onClick={onBack}>← Back to Sign In</button>
      <p style={styles.formTitle}>Reset Your Password</p>
      <p style={styles.formSubtitle}>Enter your username and we'll send a verification code to your email.</p>

      <label style={styles.label}>Username</label>
      <input style={styles.input} type="text" placeholder="Username" value={username}
        onChange={(e) => setUsername(e.target.value)} autoComplete="username" required />

      <button type="submit" style={{ ...styles.primaryBtn, ...(loading ? styles.primaryBtnDisabled : {}) }} disabled={loading}>
        {loading ? 'Sending code…' : 'Send Verification Code'}
      </button>
      {error && <div style={styles.errorMsg}>{error}</div>}
    </form>
  );
}

// ─── Forgot password — step 2: enter code + new password ─────────────────────
function ConfirmResetForm({ username, onResetSuccess, onBack }) {
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    setError('');
    try {
      await confirmForgotPassword(username, code.trim(), newPassword);
      onResetSuccess();
    } catch (err) {
      if (err.code === 'CodeMismatchException') {
        setError('Invalid verification code. Please check your email and try again.');
      } else if (err.code === 'ExpiredCodeException') {
        setError('Verification code has expired. Please request a new one.');
      } else {
        setError(err.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <button type="button" style={styles.backBtn} onClick={onBack}>← Back</button>
      <p style={styles.formTitle}>Enter Verification Code</p>
      <p style={styles.formSubtitle}>A code was sent to your email address for <strong>{username}</strong>.</p>

      <label style={styles.label}>Verification Code</label>
      <input style={styles.input} type="text" placeholder="Enter code from email" value={code}
        onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" required />

      <label style={styles.label}>New Password</label>
      <input style={styles.input} type="password" placeholder="New password" value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" required />

      <label style={styles.label}>Confirm Password</label>
      <input style={styles.input} type="password" placeholder="Confirm new password" value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" required />

      <p style={styles.hint}>Minimum 8 characters. Include uppercase, lowercase, numbers and symbols.</p>

      <button type="submit" style={{ ...styles.primaryBtn, ...(loading ? styles.primaryBtnDisabled : {}) }} disabled={loading}>
        {loading ? 'Resetting…' : 'Reset Password'}
      </button>
      {error && <div style={styles.errorMsg}>{error}</div>}
    </form>
  );
}

// ─── Password reset success ───────────────────────────────────────────────────
function ResetSuccessMessage({ onBackToLogin }) {
  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <p style={styles.formTitle}>Password Reset Successful</p>
      <div style={styles.successMsg}>Your password has been reset. You can now sign in with your new password.</div>
      <button type="button" style={{ ...styles.primaryBtn, marginTop: '20px' }} onClick={onBackToLogin}>
        Back to Sign In
      </button>
    </div>
  );
}

// ─── Main LoginPage ───────────────────────────────────────────────────────────
// view: 'login' | 'newPassword' | 'forgotStep1' | 'forgotStep2' | 'resetSuccess'
export default function LoginPage({ onLoginSuccess }) {
  const [view, setView] = useState('login');
  const [newPasswordChallenge, setNewPasswordChallenge] = useState(null);
  const [forgotUsername, setForgotUsername] = useState('');
  const [azureLoading, setAzureLoading] = useState(false);
  const [azureError, setAzureError] = useState('');

  const handleNewPasswordRequired = (cognitoUser, userAttributes) => {
    setNewPasswordChallenge({ cognitoUser, userAttributes });
    setView('newPassword');
  };

  const handleForgotPassword = (usernameFromForm) => {
    setForgotUsername(usernameFromForm || '');
    setView('forgotStep1');
  };

  const handleCodeSent = (username) => {
    setForgotUsername(username);
    setView('forgotStep2');
  };

  const handleAzureLogin = async () => {
    setAzureLoading(true);
    setAzureError('');
    try {
      const userData = await azureLogin();
      if (userData) {
        // Mark this as Azure auth and pass to parent
        onLoginSuccess({ ...userData, authType: 'azure' });
      }
    } catch (error) {
      console.error('Azure login failed', error);
      setAzureError('Azure login failed. Please try again.');
    } finally {
      setAzureLoading(false);
    }
  };

  const renderRightPanel = () => {
    switch (view) {
      case 'newPassword':
        return (
          <NewPasswordForm
            cognitoUser={newPasswordChallenge.cognitoUser}
            userAttributes={newPasswordChallenge.userAttributes}
            onLoginSuccess={onLoginSuccess}
          />
        );
      case 'forgotStep1':
        return (
          <ForgotPasswordForm
            initialUsername={forgotUsername}
            onCodeSent={handleCodeSent}
            onBack={() => setView('login')}
          />
        );
      case 'forgotStep2':
        return (
          <ConfirmResetForm
            username={forgotUsername}
            onResetSuccess={() => setView('resetSuccess')}
            onBack={() => setView('forgotStep1')}
          />
        );
      case 'resetSuccess':
        return <ResetSuccessMessage onBackToLogin={() => setView('login')} />;
      default:
        return (
          <>
            <p style={styles.panelTitle}>Sign in with your username and password</p>
            <LoginForm
              onLoginSuccess={onLoginSuccess}
              onNewPasswordRequired={handleNewPasswordRequired}
              onForgotPassword={handleForgotPassword}
            />
          </>
        );
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.logoRow}>
        <img src={designerLogo} alt="WBC Logo" style={styles.logoImg} />
        <h1 style={styles.logoText}>WBC Portal</h1>
      </div>

      <div style={styles.card}>
        {/* Left panel — Azure AD for internal employees */}
        <div style={{ ...styles.panel, ...styles.leftPanel }}>
          <p style={styles.panelTitle}>Sign in with your corporate ID</p>
          <button 
            style={{ ...styles.azureBtn, ...(azureLoading ? styles.azureBtnDisabled : {}) }}
            onClick={handleAzureLogin}
            disabled={azureLoading}
          >
            {azureLoading ? 'Signing in...' : 'Azure AD'}
          </button>
          {azureError && <div style={{ ...styles.errorMsg, marginTop: '12px' }}>{azureError}</div>}
          <span style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>For Qualcomm employees</span>
        </div>

        {/* Right panel */}
        <div style={styles.panel}>
          {renderRightPanel()}
        </div>
      </div>
    </div>
  );
}
